/**
 * What the screen on display can be asked to do from outside it — Export
 * symbols… and Paste symbols… from the command search.
 *
 * Both dialogs already exist and belong to `ScreenerView`, which holds what
 * they need: the grid's exporter, the list on screen, the refresh that makes a
 * filed symbol appear. `App` builds the action context and holds none of it.
 * So the view **publishes** two callbacks here on every render and the actions
 * call them — the dialogs stay where they are, opened by a second caller, and
 * nothing about them is rewritten (`docs/features/tracklists.md`).
 *
 * Module scope, `action-runner.ts`'s shape. `undefined` means no screener is
 * mounted — hidden, or not on this layout — and the action says so rather than
 * opening nothing.
 *
 * The gating is the view's, not repeated here: a callback is published only
 * where the screener's own ⋯ menu would offer the row. Export needs rows to
 * export; paste needs the track-list feature for this brand and account.
 */

import type { ExportedCell } from "@qufolio/screener-grid";

import type { SymbolGroup } from "../api/symbols";
import type { TrackList } from "../api/tracklists";
import type { CombineMode } from "./group-selection";

export interface ScreenerCommands {
  /** Opens Export symbols… on the screen on display; absent with nothing to export. */
  exportSymbols?: () => void;
  /** The list on screen, when the screen is exactly one of the user's own lists. */
  listOnScreen?: TrackList;
  /**
   * Reads the clipboard and opens the import dialog filled from it, filing into
   * `list`. Absent without the track-list feature. Called from the click — a
   * clipboard read needs the gesture.
   */
  pasteInto?: (list: TrackList) => void;
  /**
   * One symbol's row as the grid shows it, with the screen's name — what Train
   * freezes with a case. `undefined` when the symbol is not on the screen.
   */
  rowOf?: (symbol: string) => { screen?: string; columns: ExportedCell[] } | undefined;
  /** Every group the ◎ picker offers — what Select groups matches names against. */
  groups?: () => readonly SymbolGroup[];
  /**
   * Puts these groups in the ◎ working set, combined `or` (either) or `and`
   * (both). `add` keeps what is already picked; otherwise they replace it.
   */
  selectGroups?: (groups: readonly SymbolGroup[], combine: CombineMode, add: boolean) => void;
  /** Opens the ◎ picker on the bar, to tick groups by hand. */
  openGroupPicker?: () => void;
}

let current: ScreenerCommands | undefined;

/**
 * The mounted screener publishes; returns the withdraw function, which only
 * withdraws its own — a second view mounting first must not be cleared by the
 * first one's cleanup.
 */
export function publishScreenerCommands(next: ScreenerCommands): () => void {
  current = next;
  return () => {
    if (current === next) current = undefined;
  };
}

export function screenerCommands(): ScreenerCommands | undefined {
  return current;
}
